import React, { useState, useMemo } from 'react';
import { Calculator, Lock, Sparkles, TrendingDown } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { calculateIS } from '../../data/taxes';
import { projectAnnualProfit } from '../../utils/projectAnnualProfit';
import { VEHICLES } from '../../data/vehicles';
import { formatMoney } from '../../utils/format';

/**
 * Fiscal "what-if" simulator. Unlocked when the player has hired the
 * `fiscal_simulator` flag — currently bound to Tristan Midal (Senior
 * DAF). Locked state shows a friendly upsell hint pointing at the support
 * role hiring page.
 *
 * Mechanic :
 *   - Player picks a target vehicle (VEHICLES catalogue).
 *   - Player picks a month-from-now (1-12) at which they'd purchase it.
 *   - We compute, on the spot, the IS the player would owe in the current
 *     fiscal year (a) without the purchase, (b) with the purchase
 *     (depreciation applies from month X to month 12).
 *   - The difference is the IS saved → leitmotiv pedagogical loop.
 *
 * Single source of truth : same `calculateIS` + 5-year linear depreciation
 * formula used by `utils/depreciation.js`. We deliberately don't model the
 * accelerated mode here — the simulator is a planning tool, not a
 * production calculation.
 */
export default function FiscalSimulator() {
  const hasSupportUnlock = useGameStore(s => s.hasSupportUnlock);
  const annualProfit = useGameStore(s => s.annualProfit || 0);
  const month = useGameStore(s => s.month || 1);
  const annualDepreciation = useGameStore(s => s.annualDepreciation || 0);
  const isUnlocked = typeof hasSupportUnlock === 'function' && hasSupportUnlock('fiscal_simulator');

  const ownableVehicles = useMemo(() => VEHICLES.slice(0, 10), []);
  const [vehicleId, setVehicleId] = useState(ownableVehicles[0]?.id || '');
  const [monthsAhead, setMonthsAhead] = useState(1);

  if (!isUnlocked) {
    return (
      <div
        className="bg-ink-800 rounded-lg border border-amber-700/40 p-4"
        data-testid="fiscal-simulator-locked"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-amber-500/15">
            <Lock className="w-4 h-4 text-amber-300" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-200">Simulateur fiscal — DAF requis</p>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              Cet outil prospectif est verrouillé tant que vous n'avez pas recruté un{' '}
              <strong className="text-slate-200">Directeur administratif et financier</strong> (Tristan Midal, Senior).
              Une fois le DAF en poste, vous pourrez simuler l'impact fiscal d'un achat de véhicule sur l'IS de l'année en cours.
            </p>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              Astuce — recrutez d'abord Sophie Lambert (Comptable), prérequis pour débloquer le DAF.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Compute baseline IS (no extra purchase) and simulated IS (with purchase).
  const vehicle = VEHICLES.find(v => v.id === vehicleId);
  const remainingMonthsInYear = Math.max(0, 12 - month - monthsAhead + 1);
  const projectedAnnualProfitBase = projectAnnualProfit({ annualProfit, month }).annualProfit;
  const isBase = calculateIS(projectedAnnualProfitBase);

  // Additional depreciation from the simulated vehicle :
  // - 5-year linéaire → monthly dotation = price / 60.
  // - Applied for `remainingMonthsInYear` months in the current FY.
  // - For the projected annual extrapolation, we keep the same logic so the
  //   apprenant sees a coherent year-end picture.
  const monthlyDot = vehicle ? Math.round(vehicle.price / 60) : 0;
  const additionalDot = monthlyDot * remainingMonthsInYear;
  const projectedAnnualProfitWith = Math.max(0, projectedAnnualProfitBase - additionalDot);
  const isWith = calculateIS(projectedAnnualProfitWith);
  const isSaved = Math.max(0, isBase - isWith);

  return (
    <div className="bg-ink-800 rounded-lg border border-cyan-700/30 p-4" data-testid="fiscal-simulator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-4 h-4 text-cyan-300" />
        <p className="text-sm font-semibold text-slate-200">Simulateur fiscal — DAF</p>
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 flex items-center gap-1"
          title="Outil exclusif débloqué par votre DAF"
        >
          <Sparkles className="w-3 h-3" /> DAF exclusif
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          Véhicule simulé
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            className="bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-slate-200 text-xs"
            data-testid="fiscal-sim-vehicle"
          >
            {ownableVehicles.map(v => (
              <option key={v.id} value={v.id}>
                {v.name} — {formatMoney(v.price)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          Achat dans (mois)
          <select
            value={monthsAhead}
            onChange={(e) => setMonthsAhead(Number(e.target.value))}
            className="bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-slate-200 text-xs"
            data-testid="fiscal-sim-months"
          >
            {Array.from({ length: 12 - month + 1 }, (_, i) => i + 1).map(n => (
              <option key={n} value={n}>{n} mois</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-ink-700/60 rounded p-2 text-center">
          <p className="text-[10px] text-slate-500">IS estimé (sans achat)</p>
          <p className="text-sm font-bold text-slate-200">{formatMoney(isBase)}</p>
        </div>
        <div className="bg-ink-700/60 rounded p-2 text-center">
          <p className="text-[10px] text-slate-500">IS estimé (avec achat)</p>
          <p className="text-sm font-bold text-cyan-300">{formatMoney(isWith)}</p>
        </div>
        <div className="bg-emerald-900/30 border border-emerald-700/40 rounded p-2 text-center" data-testid="fiscal-sim-savings">
          <p className="text-[10px] text-slate-500 flex items-center justify-center gap-0.5">
            <TrendingDown className="w-3 h-3" /> Économie IS
          </p>
          <p className="text-sm font-bold text-emerald-300">{formatMoney(isSaved)}</p>
        </div>
      </div>

      <div className="text-[11px] text-slate-400 leading-relaxed">
        <strong className="text-cyan-300">Décryptage DAF&nbsp;:</strong> achat de{' '}
        <span className="text-slate-200 font-bold">{vehicle?.name}</span> à{' '}
        <span className="text-slate-200 font-bold">{formatMoney(vehicle?.price || 0)}</span> dans{' '}
        <span className="text-slate-200 font-bold">{monthsAhead} mois</span> →{' '}
        dotation supplémentaire sur l'exercice <span className="text-amber-300 font-bold">{formatMoney(additionalDot)}</span>{' '}
        (sur <span className="text-slate-200">{remainingMonthsInYear}</span> mois restants).
        Cumul existant cette année : <span className="text-amber-300">{formatMoney(Math.round(annualDepreciation))}</span>.
        {isSaved > 1000 && (
          <span className="block mt-1 text-emerald-300 italic">
            💡 Recommandation : l'investissement génère plus de {formatMoney(1000)} d'économie d'IS — étudiez sérieusement la rentabilité long terme.
          </span>
        )}
      </div>
    </div>
  );
}
